import { z } from 'zod';
import { BegehungStatus, FristArt, MangelQuelle, MangelSchwere, MangelStatus } from '../enums.js';
/**
 * Mangel — ein Befund an einem Objekt, der behoben werden muss.
 *
 * Er hängt IMMER an einer Liegenschaft und OPTIONAL an einer Anlage: Ein
 * verstellter Fluchtweg oder eine fehlende Kennzeichnung gehört zum Objekt,
 * nicht zu einem Gerät. Ein Mangel ohne Ort wäre dagegen nicht verfolgbar.
 *
 * Abgrenzung zur `RuleDeviation`: Die ist ein Befund der rechnerischen
 * Formularprüfung an einem Bauvorhaben. Der Mangel hier ist ein Befund an der
 * gebauten Wirklichkeit.
 */
export const mangel = z.object({
    id: z.number().int().positive(),
    liegenschaftId: z.number().int().positive(),
    /** Gesetzt, wenn der Mangel an einem konkreten Gerät hängt. */
    anlageId: z.number().int().positive().nullable().optional(),
    /** Gesetzt, wenn er bei einer Begehung entstanden ist. */
    begehungId: z.number().int().positive().nullable().optional(),
    quelle: MangelQuelle,
    titel: z.string(),
    beschreibung: z.string().nullable().optional(),
    /** Wo genau im Objekt — „Treppenhaus 2. OG, Tür zum Lager". */
    ortImObjekt: z.string().nullable().optional(),
    schwere: MangelSchwere,
    status: MangelStatus,
    festgestelltAm: z.string().datetime(),
    /** Bis wann zu beheben. Bei „Gefahr im Verzug" bewusst nicht erzwungen — sofort. */
    fristBis: z.string().datetime().nullable().optional(),
    /** Wer beim Kunden zuständig ist. Freitext, weil es oft kein Systembenutzer ist. */
    verantwortlich: z.string().nullable().optional(),
    /** Meldung des Betreibers, dass er es erledigt hat — noch nicht bestätigt. */
    freigemeldetAm: z.string().datetime().nullable().optional(),
    freigemeldetVon: z.string().nullable().optional(),
    freimeldungText: z.string().nullable().optional(),
    /** Bestätigung durch den Betreuer. Erst das schließt den Mangel. */
    behobenAm: z.string().datetime().nullable().optional(),
    behobenBestaetigtVon: z.number().int().positive().nullable().optional(),
    abschlussNotiz: z.string().nullable().optional(),
    /** Was zu tun ist, damit der Befund erledigt ist — die Sprache des Berichts. */
    massnahme: z.string().nullable().optional(),
    /**
     * Vorschrift oder Norm, auf die sich der Befund stützt. Freitext, weil die
     * Fundstelle fachlich verantwortet werden muss und kein Katalog sie ersetzt.
     */
    grundlage: z.string().nullable().optional(),
    /** Verbindlich (Bestellung als Brandschutzbeauftragter) oder empfohlen (Beratung). */
    fristArt: FristArt,
    /** Gesetzt, wenn der Text aus der Textbibliothek übernommen oder dorthin abgelegt wurde. */
    vorlageId: z.number().int().positive().nullable().optional(),
    /**
     * Vom Server abgeleitet: Darf dieser Mangel noch wachsen? Abgeschlossene
     * Mängel und Mängel einer abgeschlossenen Begehung sind Nachweis. Die
     * Oberfläche blendet Bearbeiten, Fotos und Vorlagenablage daran aus, statt
     * erst am 409 zu scheitern.
     */
    gesperrt: z.boolean().optional(),
    gesperrtWeil: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createMangelRequest = mangel
    .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    freigemeldetAm: true,
    freigemeldetVon: true,
    freimeldungText: true,
    behobenAm: true,
    behobenBestaetigtVon: true,
    abschlussNotiz: true,
    gesperrt: true,
    gesperrtWeil: true,
})
    .extend({
    titel: z.string().trim().min(3).max(200),
    beschreibung: z.string().max(5000).nullable().optional(),
    ortImObjekt: z.string().trim().max(200).nullable().optional(),
    verantwortlich: z.string().trim().max(200).nullable().optional(),
    quelle: MangelQuelle.default('begehung'),
    status: MangelStatus.default('offen'),
    massnahme: z.string().max(5000).nullable().optional(),
    grundlage: z.string().trim().max(500).nullable().optional(),
    fristArt: FristArt.default('empfohlen'),
});
export const updateMangelRequest = createMangelRequest.omit({ liegenschaftId: true }).partial();
/** Abschluss durch den Betreuer — bestätigt oder verwirft die Freimeldung. */
export const abschlussMangelRequest = z.object({
    entscheidung: z.enum(['behoben', 'nicht_behoben', 'hinfaellig']),
    notiz: z.string().trim().max(2000).optional(),
});
export const MANGEL_SORT_FIELDS = ['festgestelltAm', 'fristBis', 'schwere', 'status'];
export const mangelListQuery = z.object({
    liegenschaftId: z.coerce.number().int().positive().optional(),
    customerId: z.coerce.number().int().positive().optional(),
    anlageId: z.coerce.number().int().positive().optional(),
    begehungId: z.coerce.number().int().positive().optional(),
    status: MangelStatus.optional(),
    schwere: MangelSchwere.optional(),
    /** `true` = alles außer behoben und hinfällig. Der Normalfall der Liste. */
    nurOffene: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
    search: z
        .string()
        .trim()
        .max(200)
        .optional()
        .transform((v) => (v ? v : undefined)),
    sort: z
        .string()
        .trim()
        .max(40)
        .optional()
        .transform((v) => (v ? v : undefined)),
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
/**
 * Zählt dieser Mangel noch als offen?
 *
 * `freigemeldet` zählt bewusst MIT: Der Betreiber hat gemeldet, dass er
 * geliefert hat — bestätigt hat es niemand. Ihn hier herauszunehmen würde die
 * Nachschau verschwinden lassen, und damit genau den Schritt, der die Meldung
 * überhaupt erst belastbar macht.
 */
export function istOffenerMangel(status) {
    return status === 'offen' || status === 'in_bearbeitung' || status === 'freigemeldet';
}
/**
 * Rangfolge für die Anzeige: das Dringendste zuerst.
 * Kleinere Zahl = weiter oben.
 */
export const MANGEL_SCHWERE_RANG = {
    gefahr: 0,
    erheblich: 1,
    geringfuegig: 2,
    empfehlung: 3,
};
/** Obergrenze für Zusammenfassung und Fazit — gilt auch für den KI-Entwurf. */
export const BEGEHUNG_TEXT_MAX = 10000;
/**
 * Begehung — eine Brandschutzbegehung an einer Liegenschaft.
 *
 * Sie ist der wiederkehrende Vorgang, aus dem Mängel entstehen. Anders als ein
 * Projekt endet sie nicht das Objekt, sondern nur sich selbst: Nach dem
 * Abschluss ist das Protokoll schreibgeschützt, das Objekt läuft weiter.
 */
export const begehung = z.object({
    id: z.number().int().positive(),
    liegenschaftId: z.number().int().positive(),
    titel: z.string(),
    status: BegehungStatus,
    geplantAm: z.string().datetime().nullable().optional(),
    durchgefuehrtAm: z.string().datetime().nullable().optional(),
    abgeschlossenAm: z.string().datetime().nullable().optional(),
    durchgefuehrtVonId: z.number().int().positive().nullable().optional(),
    /** Wer seitens des Kunden dabei war. */
    teilnehmer: z.string().nullable().optional(),
    zusammenfassung: z.string().nullable().optional(),
    /** Fazit des Berichts — steht unter den Mängeln. */
    fazit: z.string().nullable().optional(),
    /** Stand des zuletzt erzeugten Berichts; 0 = keiner. Nur der Server setzt das. */
    berichtVersion: z.number().int().nonnegative().default(0),
    berichtErstelltAm: z.string().datetime().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * `zusammenfassung` und `fazit` sind hier änderbar — aber nur, solange die
 * Begehung nicht abgeschlossen ist. Das erzwingt die Route (409), nicht das
 * Schema: Das Schema weiß nicht, in welchem Zustand die Begehung ist.
 */
export const createBegehungRequest = begehung
    .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    abgeschlossenAm: true,
    berichtVersion: true,
    berichtErstelltAm: true,
})
    .extend({
    titel: z.string().trim().min(3).max(200),
    status: BegehungStatus.default('geplant'),
    teilnehmer: z.string().trim().max(1000).nullable().optional(),
    zusammenfassung: z.string().max(BEGEHUNG_TEXT_MAX).nullable().optional(),
    fazit: z.string().max(BEGEHUNG_TEXT_MAX).nullable().optional(),
});
export const updateBegehungRequest = createBegehungRequest.omit({ liegenschaftId: true }).partial();
/**
 * Offene Mängel der Vorbegehung, die in eine neue Begehung übernommen werden.
 *
 * Der Wunsch aus der Praxis wörtlich: „wird natürlich gleich für die nächste
 * Begehung übernommen, dass ich auch gucken kann, haben die das nur
 * abgeflogen oder ist es wirklich erledigt."
 */
export const uebernahmeVorschlag = z.object({
    mangelId: z.number().int().positive(),
    titel: z.string(),
    schwere: MangelSchwere,
    status: MangelStatus,
    festgestelltAm: z.string().datetime(),
    /** Negativ = Frist überschritten. `null`, wenn keine Frist gesetzt ist. */
    tageUeberfaellig: z.number().int().nullable(),
});
